// Story 7.18 (ADO-1387) — the decode→resolve→phase logic, factored out of the
// camera plumbing so it is fully unit-testable (AC3/AC4/AC5/AC7). ScanModal owns the
// camera + decode loop; this module owns the branching a decoded value goes through.

import type { SearchResultProduct } from '../../api/search.client';
import { resolveBarcode, type BarcodeResolution } from '../../api/barcode.client';

/** Why the camera could not be used — mapped to a distinct AC7 recovery message. */
export type CameraErrorReason = 'denied' | 'notFound' | 'unavailable';

/**
 * The scan flow's UI state. A decoded barcode maps to exactly one of these; the
 * message states (`invalid` / `notFound` / `discontinued`) are kept DISTINCT because
 * AC7 requires different copy for "not a valid barcode" vs "not in catalogue", and
 * this story owns the `discontinued` wording (project-context.md:110).
 */
export type ScanPhase =
  | { readonly status: 'scanning' }
  | { readonly status: 'resolving'; readonly barcode: string }
  | { readonly status: 'confirm'; readonly barcode: string; readonly product: SearchResultProduct }
  | {
      readonly status: 'notAvailable';
      readonly barcode: string;
      readonly product: SearchResultProduct;
    }
  | {
      readonly status: 'disambiguate';
      readonly barcode: string;
      readonly products: readonly SearchResultProduct[];
    }
  | { readonly status: 'invalid' }
  | { readonly status: 'notFound'; readonly barcode: string }
  | { readonly status: 'discontinued'; readonly barcode: string }
  | { readonly status: 'cameraError'; readonly reason: CameraErrorReason }
  | { readonly status: 'resolveError' };

/**
 * The sheet one scanned product opens (Enabler E7-C, ADO-2274, D-6): a product the server
 * marked `notAvailable` (off sale in the range, with a substitute) opens the not-available
 * sheet, which cannot add it; any other opens the confirm sheet (AC6). Used for a single
 * match and for the candidate the user picks from a disambiguation list.
 */
export function phaseForProduct(barcode: string, product: SearchResultProduct): ScanPhase {
  return product.notAvailable === true
    ? { status: 'notAvailable', barcode, product }
    : { status: 'confirm', barcode, product };
}

/**
 * Map a resolved `BarcodeResolution` to the UI phase (AC5/AC6/AC7). Pure — no I/O.
 *
 * - `unreadable` → `invalid` (bad check digit / not a barcode; AC7 "not valid").
 * - `matched` with exactly one product → `phaseForProduct`: `confirm` (AC6), or
 *   `notAvailable` for a product the server marked so (D-6).
 * - `matched` with more than one → `disambiguate` (AC5 — the user chooses, never the
 *   system). An EMPTY `matched` is defensive only (the server returns `unknown`, not
 *   an empty match) and is treated as `notFound`.
 * - `discontinued` → `discontinued` (AC7, distinct copy this story owns).
 * - `unknown` → `notFound` (valid GTIN, not in the caller's assortment; AC7).
 *
 * TOTAL by construction: the `default` returns the retryable `resolveError` so this
 * pure function can NEVER return `undefined`. `resolveBarcode` already validates the
 * wire shape, so the real path never reaches it — but `mapResolution` is exported and
 * exercised directly, and TypeScript's exhaustiveness is only compile-time. Were an
 * off-contract `kind` to reach the switch, falling through to `undefined` would make
 * `resolveScan` return `undefined` (a value, not a throw — the catch never fires) and
 * white-screen the modal. The `default` closes that at the function's own contract.
 */
export function mapResolution(res: BarcodeResolution): ScanPhase {
  switch (res.kind) {
    case 'unreadable':
      return { status: 'invalid' };
    case 'discontinued':
      return { status: 'discontinued', barcode: res.barcode };
    case 'unknown':
      return { status: 'notFound', barcode: res.barcode };
    case 'matched': {
      const [first, ...rest] = res.products;
      if (first === undefined) return { status: 'notFound', barcode: res.barcode };
      return rest.length === 0
        ? phaseForProduct(res.barcode, first)
        : { status: 'disambiguate', barcode: res.barcode, products: res.products };
    }
    default:
      return { status: 'resolveError' };
  }
}

/** Longest raw value we send to the resolution route — MIRRORS the server's
 *  `parseBarcodeParam` cap (`barcode-code.validation.ts` MAX_BARCODE_LENGTH). A GTIN
 *  is ≤ 14 digits; anything past this is not a valid barcode, so we surface the AC7
 *  "not valid" MESSAGE client-side instead of paying a round-trip for the server's
 *  400 ZodError (which the client can only map to the generic `resolveError`). */
export const MAX_BARCODE_LENGTH = 64;

/** A resolution taking longer than this is treated as a transport failure
 *  (`resolveError`), so `resolving` can't spin forever behind the green reticle on a
 *  server that accepts the request but never responds (stalled proxy / hung read) —
 *  the handheld target AC10 covers (AC7 stays reachable). */
export const RESOLVE_TIMEOUT_MS = 10_000;

/**
 * Resolve one decoded `code` and return the resulting phase. A legitimate
 * "no match" / "not valid" is a phase (a value), NEVER an error — the empty-vs-failed
 * distinction (AC7 / Dev Notes). Only a transport/non-2xx failure (including a
 * TIMEOUT) becomes `resolveError` (retryable, distinct from `notFound`). An
 * `AbortError` from the CALLER's signal (a superseded/closed scan) is re-thrown so it
 * is ignored without rendering an error; a timeout aborts with a `TimeoutError`, which
 * is NOT re-thrown and so maps to `resolveError`.
 *
 * An over-long value can never be a valid barcode (server would 400 it), so it short-
 * circuits to `invalid` — the distinct AC7 "not a valid barcode" copy, not the
 * transport-error copy — with no round-trip.
 *
 * `resolver` is injectable so tests exercise the error path without a network.
 *
 * Enabler E7-C (ADO-2274), D-1: `entry` is the selected ordering entry, whose range the
 * barcode resolves in. With none there is nothing to ask, so the scan cannot be checked.
 */
export async function resolveScan(
  entry: string | null,
  code: string,
  signal?: AbortSignal,
  resolver: typeof resolveBarcode = resolveBarcode,
): Promise<ScanPhase> {
  if (code.length > MAX_BARCODE_LENGTH) return { status: 'invalid' };
  if (entry === null) return { status: 'resolveError' };
  // One controller drives the fetch: the caller's signal (a superseded/closed scan)
  // forwards its AbortError, and the timer forwards a TimeoutError. Deliberately does
  // NOT use `AbortSignal.any` — it is unsupported on the older mobile Safari the wasm
  // ponyfill exists for, and a throw there would be swallowed into a stuck `resolving`.
  // A plain timer is fake-timer-mockable and cleared in `finally` (no dangling timer).
  const controller = new AbortController();
  const onCallerAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted) controller.abort(signal.reason);
  else signal?.addEventListener('abort', onCallerAbort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new DOMException('Barcode resolution timed out', 'TimeoutError')),
    RESOLVE_TIMEOUT_MS,
  );
  try {
    return mapResolution(await resolver(entry, code, controller.signal));
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    return { status: 'resolveError' };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }
}

/** Map a getUserMedia rejection to a distinct camera-error reason (AC7). */
export function cameraErrorReason(err: unknown): CameraErrorReason {
  const name = err instanceof DOMException ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (
    name === 'NotFoundError' ||
    name === 'OverconstrainedError' ||
    name === 'DevicesNotFoundError'
  )
    return 'notFound';
  return 'unavailable';
}

/** Copy shown OVER the live camera for the two active-scan phases (Story 7.18
 *  redesign, SCAN-02/03). `scanning` captions the white reticle + red scan line;
 *  `detected` captions the green reticle while the barcode resolves. Kept here so
 *  the copy has one home and is assertable without the camera. */
export const SCAN_STATUS_COPY = {
  scanning: 'Scanning barcode…',
  detected: {
    title: 'Barcode detected!',
    body: 'Product found — loading details…',
  },
} as const;

/** User-facing copy for each non-product phase. AC7 requires `invalid` and `notFound`
 *  to differ; `discontinued` is this story's decision (a distinct, honest message
 *  rather than folding into "not found"). Each message offers retry + text-search
 *  fallback, wired by the panel. */
export const SCAN_COPY = {
  invalid: {
    title: 'Not a valid barcode',
    body: "That isn't a valid product barcode. Try scanning again, or search by name instead.",
  },
  notFound: {
    title: 'Not in your catalogue',
    body: "We couldn't find a product with that barcode in your catalogue. Try another barcode, or search by name.",
  },
  discontinued: {
    title: 'Product discontinued',
    body: 'That barcode belongs to a product that has been discontinued and can no longer be ordered. Try another, or search by name.',
  },
  resolveError: {
    title: 'Something went wrong',
    body: "We couldn't check that barcode just now. Try again, or search by name.",
  },
  cameraDenied: {
    title: 'Camera access needed',
    body: 'Allow camera access to scan a barcode, or search by name instead.',
  },
  cameraNotFound: {
    title: 'No camera found',
    body: "We couldn't find a camera on this device. Search by name instead.",
  },
  cameraUnavailable: {
    title: 'Camera unavailable',
    body: "The camera couldn't be started. Try again, or search by name instead.",
  },
} as const;

/** The copy for a `cameraError` phase, by reason. */
export function cameraErrorCopy(reason: CameraErrorReason): { title: string; body: string } {
  switch (reason) {
    case 'denied':
      return SCAN_COPY.cameraDenied;
    case 'notFound':
      return SCAN_COPY.cameraNotFound;
    case 'unavailable':
      return SCAN_COPY.cameraUnavailable;
  }
}
